import React, { useMemo, useState } from 'react';
import { IconRefresh, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { useDisplayTimezone } from '../context/TimeModeContext';
import { todayDateStringInTz, shiftDateString, DAY_ANCHOR_TZ } from '../utils/time';
import useTeamRows from '../hooks/useTeamRows';
import TeamGrid from '../components/TeamGrid';
import TodayBadge from '../components/TodayBadge';
import Layout from '../components/Layout';

export default function AdminDashboard() {
  const { currentUser, refetch } = useApp();
  const { showSuccess } = useToast();
  const displayTz = useDisplayTimezone(currentUser);

  // The date filter (and "today") are always anchored to CST calendar days
  // — the team's shared operational "day" — regardless of the CST/local
  // display toggle, which only affects how individual times are labeled.
  const todayCst = useMemo(() => todayDateStringInTz(DAY_ANCHOR_TZ), []);
  const [selectedDate, setSelectedDate] = useState(todayCst);

  const teamRows = useTeamRows(selectedDate);

  const counts = useMemo(() => {
    return teamRows.reduce(
      (acc, row) => {
        acc[row.status] += 1;
        return acc;
      },
      { in: 0, out: 0, none: 0 }
    );
  }, [teamRows]);

  const handleRefetch = async () => {
    await refetch();
    showSuccess('Team status refreshed.');
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-10">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-ink">Team status</h1>
            <p className="text-xs sm:text-sm text-muted mt-1">Viewing in {displayTz} time.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <TodayBadge timezone={DAY_ANCHOR_TZ} />
            <button
              onClick={handleRefetch}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm font-medium text-ink hover:bg-card-tint transition-colors flex-shrink-0"
            >
              <IconRefresh size={16} stroke={1.8} />
              Refresh
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="text-xs font-medium text-muted uppercase tracking-wide">CST date</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSelectedDate((d) => shiftDateString(d, -1))}
              title="Previous day"
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-border text-ink hover:bg-card-tint transition-colors"
            >
              <IconChevronLeft size={16} stroke={1.8} />
            </button>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={todayCst}
              className="border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
            />
            <button
              onClick={() => setSelectedDate((d) => shiftDateString(d, 1))}
              disabled={selectedDate >= todayCst}
              title="Next day"
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-border text-ink hover:bg-card-tint transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <IconChevronRight size={16} stroke={1.8} />
            </button>
          </div>
          {selectedDate !== todayCst && (
            <button
              onClick={() => setSelectedDate(todayCst)}
              className="text-xs font-medium text-bronze hover:text-ink transition-colors"
            >
              Back to today
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6">
          <StatCard label="Checked in" value={counts.in} tone="success" />
          <StatCard label="Checked out" value={counts.out} tone="neutral" />
          <StatCard label="Not yet" value={counts.none} tone="neutral" />
        </div>

        <div className="mt-8">
          <TeamGrid teamRows={teamRows} showNotes showProfileOnClick displayTimezone={displayTz} />
        </div>
      </div>
    </Layout>
  );
}

function StatCard({ label, value, tone }) {
  const toneClasses = tone === 'success' ? 'text-success-tx' : 'text-neutral-tx';
  return (
    <div className="bg-card border border-border rounded-xl p-3 sm:p-4 text-center">
      <p className={`text-xl sm:text-2xl font-semibold ${toneClasses}`}>{value}</p>
      <p className="text-[11px] sm:text-xs text-muted mt-1">{label}</p>
    </div>
  );
}
